// ─────────────────────────────────────────────────────────────
// AccountManager — three tabs:
//   1. Accounts      — add/manage trading accounts
//   2. ROI Tracker   — firm-level payout vs expense rollup + charts
//   3. Payouts & Expenses — log individual transactions
// ─────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Btn } from "../../components/ui/Btn";
import { Card } from "../../components/ui/Card";
import { C } from "../../lib/colors";
import { SUPABASE_URL, authFetch } from "../../lib/supabase";

// ─── Shared input style ───────────────────────────────────────
const inp = (extra = {}) => ({
  width: "100%",
  padding: "10px 12px",
  background: "#1a1d2e",
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  color: C.text,
  fontSize: 13,
  marginBottom: 8,
  boxSizing: "border-box",
  ...extra,
});

// ─── Known prop firms pulled from prop_firm_presets firm_name values ──
const KNOWN_FIRMS = [
  "Apex", "TopStep", "Bulenox", "Take Profit Trader",
  "MyFundedFutures", "Lucid Trading", "Earn2Trade", "Uprofit", "Leeloo",
  "EdgeClear", "TradeDay", "Other",
];

// ─── Compliance presets per firm + account size ───────────────
// Values are best-effort defaults based on published rules as of mid-2026.
// Users should verify against their firm's dashboard — rules change.
const FIRM_COMPLIANCE_PRESETS = {
  // Apex current accounts default to Intraday Trail (no daily loss limit).
  // EOD Trail uses the same drawdown amounts but adds a daily loss limit —
  // user should set drawdown_type to "eod_trailing" and daily_loss_limit manually
  // if they have an EOD account. Legacy accounts have different drawdown amounts.
  "Apex": {
    "25000":  { drawdown_limit: 1000, drawdown_type: "intraday_trailing", daily_loss_limit: null, profit_target: 1500, consistency_pct: 50 },
    "50000":  { drawdown_limit: 2000, drawdown_type: "intraday_trailing", daily_loss_limit: null, profit_target: 3000, consistency_pct: 50 },
    "100000": { drawdown_limit: 3000, drawdown_type: "intraday_trailing", daily_loss_limit: null, profit_target: 6000, consistency_pct: 50 },
    "150000": { drawdown_limit: 4000, drawdown_type: "intraday_trailing", daily_loss_limit: null, profit_target: 9000, consistency_pct: 50 },
    "250000": { drawdown_limit: 6500, drawdown_type: "intraday_trailing", daily_loss_limit: null, profit_target: 15000, consistency_pct: 50 },
  },
  "TopStep": {
    "50000":  { drawdown_limit: 2000,  drawdown_type: "intraday_trailing", daily_loss_limit: null, profit_target: 3000, consistency_pct: 50 },
    "100000": { drawdown_limit: 3000,  drawdown_type: "intraday_trailing", daily_loss_limit: null, profit_target: 6000, consistency_pct: 50 },
    "150000": { drawdown_limit: 4500,  drawdown_type: "intraday_trailing", daily_loss_limit: null, profit_target: 9000, consistency_pct: 50 },
  },
  "Take Profit Trader": {
    "25000":  { drawdown_limit: 1500,  drawdown_type: "eod_trailing",      daily_loss_limit: null, profit_target: 1500, consistency_pct: 0 },
    "50000":  { drawdown_limit: 2000,  drawdown_type: "eod_trailing",      daily_loss_limit: null, profit_target: 3000, consistency_pct: 0 },
    "75000":  { drawdown_limit: 3000,  drawdown_type: "eod_trailing",      daily_loss_limit: null, profit_target: 4500, consistency_pct: 0 },
    "100000": { drawdown_limit: 4000,  drawdown_type: "eod_trailing",      daily_loss_limit: null, profit_target: 6000, consistency_pct: 0 },
    "150000": { drawdown_limit: 5000,  drawdown_type: "eod_trailing",      daily_loss_limit: null, profit_target: 9000, consistency_pct: 0 },
  },
  "MyFundedFutures": {
    "25000":  { drawdown_limit: 1500,  drawdown_type: "eod_trailing",      daily_loss_limit: null, profit_target: 1500, consistency_pct: 50 },
    "50000":  { drawdown_limit: 3000,  drawdown_type: "eod_trailing",      daily_loss_limit: null, profit_target: 3000, consistency_pct: 50 },
    "100000": { drawdown_limit: 5000,  drawdown_type: "eod_trailing",      daily_loss_limit: null, profit_target: 6000, consistency_pct: 50 },
    "150000": { drawdown_limit: 7500,  drawdown_type: "eod_trailing",      daily_loss_limit: null, profit_target: 9000, consistency_pct: 50 },
  },
  "Bulenox": {
    "25000":  { drawdown_limit: 1500,  drawdown_type: "intraday_trailing", daily_loss_limit: null, profit_target: 1500, consistency_pct: 40 },
    "50000":  { drawdown_limit: 2500,  drawdown_type: "intraday_trailing", daily_loss_limit: null, profit_target: 3000, consistency_pct: 40 },
    "100000": { drawdown_limit: 4000,  drawdown_type: "intraday_trailing", daily_loss_limit: null, profit_target: 6000, consistency_pct: 40 },
    "150000": { drawdown_limit: 5500,  drawdown_type: "intraday_trailing", daily_loss_limit: null, profit_target: 9000, consistency_pct: 40 },
    "250000": { drawdown_limit: 8000,  drawdown_type: "intraday_trailing", daily_loss_limit: null, profit_target: 15000, consistency_pct: 40 },
  },
  "Lucid Trading": {
    "25000":  { drawdown_limit: 1500,  drawdown_type: "eod_trailing",      daily_loss_limit: null, profit_target: 1500, consistency_pct: 40 },
    "50000":  { drawdown_limit: 2500,  drawdown_type: "eod_trailing",      daily_loss_limit: null, profit_target: 3000, consistency_pct: 40 },
    "100000": { drawdown_limit: 4000,  drawdown_type: "eod_trailing",      daily_loss_limit: null, profit_target: 6000, consistency_pct: 40 },
    "150000": { drawdown_limit: 5500,  drawdown_type: "eod_trailing",      daily_loss_limit: null, profit_target: 9000, consistency_pct: 40 },
  },
};

// Find the closest account-size key in a firm's presets
const getPresetForFirmAndBalance = (firm, balance) => {
  const firmPresets = FIRM_COMPLIANCE_PRESETS[firm];
  if (!firmPresets) return null;
  const bal = Math.round(parseFloat(balance) || 0);
  // Exact match first
  if (firmPresets[String(bal)]) return firmPresets[String(bal)];
  // Closest match by nearest key
  const keys = Object.keys(firmPresets).map(Number).sort((a, b) => a - b);
  const closest = keys.reduce((prev, curr) =>
    Math.abs(curr - bal) < Math.abs(prev - bal) ? curr : prev, keys[0]);
  return firmPresets[String(closest)] || null;
};

// ─── Currency formatter ───────────────────────────────────────
const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n || 0);

const pct = (n) => `${n >= 0 ? "+" : ""}${(n || 0).toFixed(1)}%`;

// ─────────────────────────────────────────────────────────────
// TAB BAR
// ─────────────────────────────────────────────────────────────
function TabBar({ active, onChange }) {
  const tabs = [
    { id: "accounts", label: "🏦 Accounts" },
    { id: "roi",      label: "📊 ROI Tracker" },
    { id: "txns",     label: "💸 Payouts & Expenses" },
  ];
  return (
    <div style={{
      display: "flex", gap: 4, marginBottom: 24,
      borderBottom: `1px solid ${C.border}`, paddingBottom: 0,
    }}>
      {tabs.map(t => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            padding: "10px 16px",
            background: "none",
            border: "none",
            borderBottom: active === t.id ? `2px solid ${C.blue}` : "2px solid transparent",
            color: active === t.id ? C.blue : C.muted,
            fontSize: 13,
            fontWeight: active === t.id ? 700 : 500,
            cursor: "pointer",
            marginBottom: -1,
            transition: "all 0.15s",
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
export function AccountManager({ userId, setView, accounts, setAccounts, defaultTab }) {
  const [tab, setTab] = useState(defaultTab || "accounts");
  const [payouts, setPayouts]   = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loadingTxns, setLoadingTxns] = useState(false);
  const [blownAccounts, setBlownAccounts] = useState([]);
  const [passedAccounts, setPassedAccounts] = useState([]);

  // ── Fetch blown/archived accounts ────────────────────────
  const fetchBlownAccounts = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await authFetch(
        `${SUPABASE_URL}/rest/v1/accounts?user_id=eq.${userId}&is_blown=eq.true&order=blown_at.desc`
      );
      const data = await res.json();
      setBlownAccounts(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to fetch blown accounts", e);
    }
  }, [userId]);

  // ── Fetch passed eval/combine accounts (converted → funded) ──
  const fetchPassedAccounts = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await authFetch(
        `${SUPABASE_URL}/rest/v1/accounts?user_id=eq.${userId}&is_passed=eq.true&order=passed_at.desc`
      );
      const data = await res.json();
      setPassedAccounts(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to fetch passed accounts", e);
    }
  }, [userId]);

  // ── Fetch accounts ────────────────────────────────────────
  const fetchAccounts = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await authFetch(
        `${SUPABASE_URL}/rest/v1/accounts?user_id=eq.${userId}&is_active=eq.true&is_blown=not.eq.true&is_passed=not.eq.true&order=created_at.asc`
      );
      const data = await res.json();
      setAccounts(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to fetch accounts", e);
      setAccounts([]);
    }
  }, [userId, setAccounts]);

  // ── Fetch payouts + expenses ──────────────────────────────
  const fetchTxns = useCallback(async () => {
    if (!userId) return;
    setLoadingTxns(true);
    try {
      const [pRes, eRes] = await Promise.all([
        authFetch(`${SUPABASE_URL}/rest/v1/payouts?user_id=eq.${userId}&order=date.desc`),
        authFetch(`${SUPABASE_URL}/rest/v1/expenses?user_id=eq.${userId}&order=date.desc`),
      ]);
      const [pd, ed] = await Promise.all([pRes.json(), eRes.json()]);
      setPayouts(Array.isArray(pd) ? pd : []);
      setExpenses(Array.isArray(ed) ? ed : []);
    } catch (e) {
      console.error("Failed to fetch transactions", e);
    } finally {
      setLoadingTxns(false);
    }
  }, [userId]);

  useEffect(() => { fetchAccounts(); fetchBlownAccounts(); fetchPassedAccounts(); }, [fetchAccounts, fetchBlownAccounts, fetchPassedAccounts]);
  useEffect(() => { if (tab === "roi" || tab === "txns") fetchTxns(); }, [tab, fetchTxns]);

  return (
    <div style={{ maxWidth: 860, margin: "0 auto", padding: "20px 16px" }}>
      <button
        onClick={() => setView("dashboard")}
        style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 13, marginBottom: 16 }}
      >
        ← Back to Dashboard
      </button>
      <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 20, color: C.text }}>
        Portfolio
      </h2>

      <TabBar active={tab} onChange={setTab} />

      {tab === "accounts" && (
        <AccountsTab
          userId={userId}
          accounts={accounts}
          fetchAccounts={fetchAccounts}
          blownAccounts={blownAccounts}
          fetchBlownAccounts={fetchBlownAccounts}
          passedAccounts={passedAccounts}
          fetchPassedAccounts={fetchPassedAccounts}
        />
      )}
      {tab === "roi" && (
        <ROITab
          accounts={accounts}
          payouts={payouts}
          expenses={expenses}
          loading={loadingTxns}
        />
      )}
      {tab === "txns" && (
        <TxnsTab
          userId={userId}
          accounts={accounts}
          payouts={payouts}
          expenses={expenses}
          fetchTxns={fetchTxns}
          fetchAccounts={fetchAccounts}
          loading={loadingTxns}
        />
      )}
    </div>
  );
}

// ─── Known broker platforms ───────────────────────────────────
const KNOWN_PLATFORMS = [
  "Rithmic", "Tradovate", "NinjaTrader", "Quantower",
  "TradeStation", "Sierra Chart", "Bookmap", "Finamark",
  "Jigsaw", "Other",
];

// ─────────────────────────────────────────────────────────────
// TAB 1 — ACCOUNTS
// ─────────────────────────────────────────────────────────────
function AccountsTab({ userId, accounts, fetchAccounts, blownAccounts = [], fetchBlownAccounts, passedAccounts = [], fetchPassedAccounts }) {
  const [newName,       setNewName]       = useState("");
  const [newType,       setNewType]       = useState("funded");
  const [newBalance,    setNewBalance]    = useState(50000);
  const [newSplit,      setNewSplit]      = useState(80);
  const [editAccount,   setEditAccount]   = useState(null);

  // Prop firm — dropdown + custom entry when "Other" selected
  const [firmSelect,    setFirmSelect]    = useState("");
  const [firmCustom,    setFirmCustom]    = useState("");
  const firmValue = firmSelect === "Other" ? firmCustom : firmSelect;

  // Platform — dropdown + custom entry when "Other" selected
  const [platSelect,    setPlatSelect]    = useState("");
  const [platCustom,    setPlatCustom]    = useState("");
  const platValue = platSelect === "Other" ? platCustom : platSelect;

  // ── Compliance fields ───────────────────────────────────────
  const [ddLimit,         setDdLimit]         = useState("");
  const [ddType,          setDdType]          = useState("eod_trailing");
  const [dailyLossLimit,  setDailyLossLimit]  = useState("");
  const [profitTarget,    setProfitTarget]    = useState("");
  const [consistencyRule, setConsistencyRule] = useState("");

  // Auto-fill compliance toggle — mirrors the daily rules preset pattern
  const [autoFillCompliance, setAutoFillCompliance] = useState(false);

  const applyFirmPreset = (firm, balance) => {
    const preset = getPresetForFirmAndBalance(firm, balance);
    if (!preset) return;
    setDdLimit(preset.drawdown_limit != null ? String(preset.drawdown_limit) : "");
    setDdType(preset.drawdown_type || "eod_trailing");
    setDailyLossLimit(preset.daily_loss_limit != null ? String(preset.daily_loss_limit) : "");
    setProfitTarget(preset.profit_target != null ? String(preset.profit_target) : "");
    setConsistencyRule(preset.consistency_pct != null ? String(preset.consistency_pct) : "");
  };

  // Re-apply whenever firm or balance changes while auto-fill is on
  useEffect(() => {
    if (autoFillCompliance && firmValue && newBalance) {
      applyFirmPreset(firmValue, newBalance);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFillCompliance, firmValue, newBalance]);

  const addAccount = async () => {
    if (!newName.trim()) return alert("Account name is required");
    if (firmSelect === "Other" && !firmCustom.trim()) return alert("Enter the prop firm name");
    if (platSelect === "Other" && !platCustom.trim()) return alert("Enter the platform name");

    // Check for duplicate name
    try {
      const res = await authFetch(
        `${SUPABASE_URL}/rest/v1/accounts?user_id=eq.${userId}&name=eq.${encodeURIComponent(newName.trim())}&is_active=eq.true`
      );
      const existing = await res.json();
      if (Array.isArray(existing) && existing.length > 0) {
        return alert("An account with this name already exists. Please use a different name.");
      }
    } catch (e) {
      // Non-blocking
    }
    try {
      const payload = {
        user_id:          userId,
        name:             newName.trim(),
        broker:           platValue.trim() || null,
        account_type:     newType,
        starting_balance: parseFloat(newBalance) || 0,
        prop_firm:        firmValue.trim() || null,
        payout_split:     parseFloat(newSplit) / 100 || 0.80,
        // compliance fields
        drawdown_limit:   parseFloat(ddLimit) || null,
        drawdown_type:    ddType || "eod_trailing",
        daily_loss_limit: parseFloat(dailyLossLimit) || null,
        profit_target:    parseFloat(profitTarget) || null,
        consistency_rule: consistencyRule !== "" ? parseFloat(consistencyRule) / 100 : null,
      };
      const res = await authFetch(`${SUPABASE_URL}/rest/v1/accounts`, {
        method: "POST",
        headers: { Prefer: "return=representation" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setNewName(""); setNewBalance(50000); setNewSplit(80);
        setFirmSelect(""); setFirmCustom("");
        setPlatSelect(""); setPlatCustom("");
        setDdLimit(""); setDailyLossLimit(""); setProfitTarget(""); setConsistencyRule("");
        setAutoFillCompliance(false);
        fetchAccounts();
      } else {
        const err = await res.json();
        alert("Failed: " + (err.message || err.error));
      }
    } catch (e) {
      alert("Network error");
    }
  };

  const deleteAccount = async (id, name) => {
    if (!window.confirm(`Delete account "${name}"?`)) return;
    if (!window.confirm(`Also delete ALL trades linked to this account?\n\nClick OK to delete trades, or Cancel to abort.`)) {
      alert("Account deletion cancelled. Your trades are safe.");
      return;
    }
    try {
      await authFetch(`${SUPABASE_URL}/rest/v1/trades?account_id=eq.${id}`, { method: "DELETE" });
      await authFetch(`${SUPABASE_URL}/rest/v1/accounts?id=eq.${id}`, { method: "DELETE" });
      fetchAccounts();
    } catch (e) {
      alert("Failed to delete account");
    }
  };

  const [blownAccountTarget, setBlownAccountTarget] = useState(null);

  const markAsBlown = async ({ accountId, blownAt, reason }) => {
    try {
      await authFetch(`${SUPABASE_URL}/rest/v1/accounts?id=eq.${accountId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Prefer: "return=minimal" },
        body: JSON.stringify({
          is_blown:    true,
          blown_at:    blownAt,
          blown_reason: reason || null,
        }),
      });
      setBlownAccountTarget(null);
      fetchAccounts();
      fetchBlownAccounts();
    } catch (e) {
      alert("Failed to mark account as blown: " + e.message);
    }
  };

  const restoreAccount = async (id) => {
    if (!window.confirm("Restore this account to active?")) return;
    try {
      await authFetch(`${SUPABASE_URL}/rest/v1/accounts?id=eq.${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Prefer: "return=minimal" },
        body: JSON.stringify({ is_blown: false, blown_at: null, blown_reason: null }),
      });
      fetchAccounts();
      fetchBlownAccounts();
    } catch (e) {
      alert("Failed to restore account: " + e.message);
    }
  };

  // ── Convert a passed eval/combine into a Funded account ──────
  // Replaces the old delete-and-recreate flow. The eval/combine
  // account is never deleted — it's flagged is_passed and kept
  // (with all its trades) for its own analytics. A new Funded
  // account is created and linked via lineage_id/parent_account_id
  // so the two can also be viewed together.
  const [passAccountTarget, setPassAccountTarget] = useState(null);

  const passAndConvert = async ({ sourceAccount, passedAt, funded }) => {
    const lineageId = sourceAccount.lineage_id || sourceAccount.id;
    try {
      // 0. Duplicate-name check — same rule as the main Add Account form.
      const dupRes = await authFetch(
        `${SUPABASE_URL}/rest/v1/accounts?user_id=eq.${userId}&name=eq.${encodeURIComponent(funded.name.trim())}&is_active=eq.true`
      );
      const dup = await dupRes.json();
      if (Array.isArray(dup) && dup.length > 0) {
        throw new Error("An account with this name already exists. Please use a different name.");
      }

      // 1. Create the new Funded account, linked to the source.
      const createRes = await authFetch(`${SUPABASE_URL}/rest/v1/accounts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Prefer: "return=representation" },
        body: JSON.stringify({
          user_id:           userId,
          name:               funded.name.trim(),
          broker:             funded.broker || null,
          account_type:       "funded",
          starting_balance:   funded.starting_balance,
          prop_firm:          sourceAccount.prop_firm || null,
          payout_split:       funded.payout_split,
          drawdown_limit:     funded.drawdown_limit,
          drawdown_type:      funded.drawdown_type || "eod_trailing",
          daily_loss_limit:   funded.daily_loss_limit,
          profit_target:      null,
          consistency_rule:   funded.consistency_rule,
          lineage_id:         lineageId,
          parent_account_id:  sourceAccount.id,
        }),
      });
      if (!createRes.ok) {
        const err = await createRes.json();
        throw new Error(err.message || err.error || "Failed to create funded account");
      }

      // 2. Flag the source eval/combine as passed — never deleted,
      //    trades stay put, it just drops out of the active list.
      const patchRes = await authFetch(`${SUPABASE_URL}/rest/v1/accounts?id=eq.${sourceAccount.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Prefer: "return=minimal" },
        body: JSON.stringify({
          is_passed:  true,
          passed_at:  passedAt,
          lineage_id: lineageId,
        }),
      });
      if (!patchRes.ok) {
        const err = await patchRes.json();
        throw new Error(err.message || err.error || "Failed to mark source account as passed");
      }

      setPassAccountTarget(null);
      fetchAccounts();
      fetchPassedAccounts();
    } catch (e) {
      alert("Failed to convert account: " + e.message);
    }
  };

  // ── Undo a Passed conversion ──────────────────────────────────
  // Deletes the Funded account it created (and any trades logged on
  // it since) and flips the source eval/combine back to active.
  // Mirrors deleteAccount's two-step confirm since this is destructive
  // on the funded side.
  const undoPass = async (passedAccount) => {
    const child = accounts.find(x => x.parent_account_id === passedAccount.id);
    if (!child) {
      alert("Couldn't find the linked funded account — nothing to undo automatically. Check the account list manually.");
      return;
    }
    if (!window.confirm(
      `Undo "${passedAccount.name}" → "${child.name}"?\n\nThis will permanently delete "${child.name}" and any trades logged on it. "${passedAccount.name}" will move back to your active accounts.`
    )) return;

    try {
      await authFetch(`${SUPABASE_URL}/rest/v1/trades?account_id=eq.${child.id}`, { method: "DELETE" });
      await authFetch(`${SUPABASE_URL}/rest/v1/accounts?id=eq.${child.id}`, { method: "DELETE" });
      await authFetch(`${SUPABASE_URL}/rest/v1/accounts?id=eq.${passedAccount.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Prefer: "return=minimal" },
        body: JSON.stringify({ is_passed: false, passed_at: null }),
      });
      fetchAccounts();
      fetchPassedAccounts();
    } catch (e) {
      alert("Failed to undo: " + e.message);
    }
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
      {/* Add form */}
      <Card>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14, color: C.text }}>Add New Account</div>
        <input placeholder="Account Name (e.g. Apex $50K #2)" value={newName} onChange={e => setNewName(e.target.value)} style={inp()} />

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Prop Firm</label>
        <select value={firmSelect} onChange={e => { setFirmSelect(e.target.value); setFirmCustom(""); setAutoFillCompliance(false); }} style={inp()}>
          <option value="">— Select firm —</option>
          {KNOWN_FIRMS.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        {firmSelect === "Other" && (
          <input
            placeholder="Enter prop firm name"
            value={firmCustom}
            onChange={e => setFirmCustom(e.target.value)}
            style={inp({ borderColor: C.blue, marginTop: -4 })}
            autoFocus
          />
        )}

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Account Type</label>
        <select value={newType} onChange={e => setNewType(e.target.value)} style={inp()}>
          <option value="live">Live Account</option>
          <option value="sim">Sim Account</option>
          <option value="prop">Prop Eval</option>
          <option value="funded">Funded Account</option>
        </select>

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Platform</label>
        <select value={platSelect} onChange={e => { setPlatSelect(e.target.value); setPlatCustom(""); }} style={inp()}>
          <option value="">— Select platform —</option>
          {KNOWN_PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        {platSelect === "Other" && (
          <input
            placeholder="Enter platform name"
            value={platCustom}
            onChange={e => setPlatCustom(e.target.value)}
            style={inp({ borderColor: C.blue, marginTop: -4 })}
            autoFocus
          />
        )}

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Starting Balance ($)</label>
        <input type="number" value={newBalance} onChange={e => setNewBalance(e.target.value)} style={inp()} />

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>
          Your payout split (%) — e.g. 80 means you keep 80%
        </label>
        <input type="number" min={0} max={100} value={newSplit} onChange={e => setNewSplit(e.target.value)} style={inp()} />

        {/* ── Compliance Setup (optional but recommended) ──────── */}
        <div style={{ marginTop: 6, padding: "12px", background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 8 }}>
          {/* Header row with toggle */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#60a5fa", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Compliance Setup{" "}
              <span style={{ fontWeight: 400, color: C.muted, textTransform: "none", letterSpacing: 0 }}>
                (optional — enables the compliance dashboard widget)
              </span>
            </div>
            {/* Auto-fill toggle — only shown when firm has presets */}
            {FIRM_COMPLIANCE_PRESETS[firmValue] && (
              <button
                onClick={() => {
                  const next = !autoFillCompliance;
                  setAutoFillCompliance(next);
                  if (next) applyFirmPreset(firmValue, newBalance);
                  else {
                    setDdLimit(""); setDdType("eod_trailing");
                    setDailyLossLimit(""); setProfitTarget(""); setConsistencyRule("");
                  }
                }}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "4px 10px",
                  background: autoFillCompliance ? "rgba(59,130,246,0.18)" : "transparent",
                  border: `1px solid ${autoFillCompliance ? "#60a5fa" : C.border}`,
                  borderRadius: 6,
                  color: autoFillCompliance ? "#60a5fa" : C.muted,
                  fontSize: 11, fontWeight: 600, cursor: "pointer",
                  flexShrink: 0, whiteSpace: "nowrap",
                }}
              >
                <span style={{
                  display: "inline-block", width: 28, height: 16, borderRadius: 8,
                  background: autoFillCompliance ? "#3b82f6" : C.border,
                  position: "relative", transition: "background 0.2s", flexShrink: 0,
                }}>
                  <span style={{
                    position: "absolute", top: 2,
                    left: autoFillCompliance ? 14 : 2,
                    width: 12, height: 12, borderRadius: "50%",
                    background: "#fff", transition: "left 0.2s",
                  }} />
                </span>
                Auto-fill from {firmValue}
              </button>
            )}
          </div>

          {/* Auto-fill active notice */}
          {autoFillCompliance && FIRM_COMPLIANCE_PRESETS[firmValue] && (
            <div style={{
              fontSize: 11, color: "#60a5fa",
              background: "rgba(59,130,246,0.08)",
              border: "1px solid rgba(59,130,246,0.2)",
              borderRadius: 6, padding: "6px 10px", marginBottom: 10, lineHeight: 1.5,
            }}>
              ✓ Pre-filled from <strong>{firmValue}</strong> defaults for a {
                new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(parseFloat(newBalance) || 0)
              } account. Verify against your firm's dashboard — rules change.
              You can override any field below.
            </div>
          )}

          <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Max Drawdown Limit ($)</label>
          <input type="number" value={ddLimit} onChange={e => setDdLimit(e.target.value)} placeholder="e.g. 2500" style={inp()} />

          <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Drawdown Type</label>
          <select value={ddType} onChange={e => setDdType(e.target.value)} style={inp()}>
            <option value="eod_trailing">EOD Trailing (most common — Topstep, Apex, MFF)</option>
            <option value="static">Static (floor never moves)</option>
            <option value="intraday_trailing">Intraday Trailing (real-time — Apex Intraday)</option>
          </select>

          <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Daily Loss Limit ($) <span style={{ color: C.muted, fontWeight: 400 }}>(optional)</span></label>
          <input type="number" value={dailyLossLimit} onChange={e => setDailyLossLimit(e.target.value)} placeholder="e.g. 1000" style={inp()} />

          <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Profit Target ($) <span style={{ color: C.muted, fontWeight: 400 }}>(optional — for eval accounts)</span></label>
          <input type="number" value={profitTarget} onChange={e => setProfitTarget(e.target.value)} placeholder="e.g. 3000" style={inp()} />

          <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Consistency Rule (%) <span style={{ color: C.muted, fontWeight: 400 }}>(optional — 0 = none, 50 = Topstep/Apex)</span></label>
          <input type="number" min={0} max={100} value={consistencyRule} onChange={e => setConsistencyRule(e.target.value)} placeholder="e.g. 50" style={inp()} />

          {/* Disclaimer */}
          <div style={{
            marginTop: 10,
            padding: "8px 10px",
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 6,
            fontSize: 11,
            color: C.muted,
            lineHeight: 1.6,
          }}>
            📋 You are responsible for keeping these settings current. Prop firm rules can change — verify and update accordingly against your firm's dashboard any time you receive a rule update notice and before you trade.
          </div>
        </div>

        <Btn onClick={addAccount} style={{ width: "100%", marginTop: 4 }}>+ Add Account</Btn>
      </Card>

      {/* Account list */}
      <Card>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14, color: C.text }}>
          Your Accounts ({accounts.length})
        </div>
        {accounts.length === 0 && (
          <div style={{ color: C.muted, fontSize: 13 }}>No accounts yet.</div>
        )}
        {accounts.map(a => (
          <div key={a.id} style={{
            display: "flex", justifyContent: "space-between", alignItems: "flex-start",
            padding: "12px 0", borderBottom: `1px solid ${C.border}`,
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{a.name}</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 3, lineHeight: 1.6 }}>
                {a.prop_firm && <span style={{ color: C.blue, marginRight: 6 }}>{a.prop_firm}</span>}
                {a.account_type?.toUpperCase()} • {fmt(a.starting_balance)}
                {a.payout_split && ` • ${Math.round(a.payout_split * 100)}% split`}
                {a.broker && ` • ${a.broker}`}
                {a.drawdown_limit && <span style={{ color: '#22c55e', marginLeft: 6, fontSize: 10 }}>📊 DD {fmt(a.drawdown_limit)}</span>}
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
              <button
                onClick={() => setEditAccount(a)}
                style={{ color: C.blue, background: "none", border: "none", cursor: "pointer", fontSize: 12 }}
              >
                Edit
              </button>
              {a.account_type === "prop" && (
                <button
                  onClick={() => setPassAccountTarget(a)}
                  style={{ color: C.green, background: "none", border: "none", cursor: "pointer", fontSize: 12 }}
                >
                  Passed
                </button>
              )}
              <button
                onClick={() => setBlownAccountTarget(a)}
                style={{ color: C.yellow, background: "none", border: "none", cursor: "pointer", fontSize: 12 }}
              >
                Blown
              </button>
              <button
                onClick={() => deleteAccount(a.id, a.name)}
                style={{ color: C.red, background: "none", border: "none", cursor: "pointer", fontSize: 12 }}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </Card>

      {/* Edit modal */}
      {editAccount && (
        <EditAccountModal
          account={editAccount}
          onClose={() => setEditAccount(null)}
          onSaved={() => { setEditAccount(null); fetchAccounts(); }}
        />
      )}

      {/* Blown account modal */}
      {blownAccountTarget && (
        <BlownAccountModal
          account={blownAccountTarget}
          onClose={() => setBlownAccountTarget(null)}
          onConfirm={markAsBlown}
        />
      )}

      {/* Pass eval/combine → convert to Funded modal */}
      {passAccountTarget && (
        <PassAccountModal
          account={passAccountTarget}
          onClose={() => setPassAccountTarget(null)}
          onConfirm={passAndConvert}
        />
      )}

      {/* Passed accounts section — full width below the two-column grid */}
      {passedAccounts.length > 0 && (
        <div style={{ gridColumn: "1 / -1", marginTop: 8 }}>
          <Card>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4, color: C.muted }}>
              🎓 Passed Evals / Combines ({passedAccounts.length})
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 14, fontStyle: "italic" }}>
              Trade history and analytics are preserved separately from the Funded account they converted into.
            </div>
            {passedAccounts.map(a => {
              const child = accounts.find(x => x.parent_account_id === a.id);
              return (
                <div key={a.id} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                  padding: "12px 0", borderBottom: `1px solid ${C.border}20`,
                }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: C.sub }}>{a.name}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: "2px 7px",
                        background: C.green + "20", color: C.green,
                        borderRadius: 4, letterSpacing: "0.06em",
                      }}>
                        PASSED
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 3, lineHeight: 1.6 }}>
                      {a.prop_firm && <span style={{ color: C.blue, marginRight: 6 }}>{a.prop_firm}</span>}
                      {a.account_type?.toUpperCase()} • {fmt(a.starting_balance)}
                      {a.passed_at && ` • Passed ${new Date(a.passed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`}
                      {child && <span style={{ color: C.green, marginLeft: 6 }}>→ {child.name}</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => undoPass(a)}
                    style={{ color: C.yellow, background: "none", border: "none", cursor: "pointer", fontSize: 12, flexShrink: 0 }}
                  >
                    Undo
                  </button>
                </div>
              );
            })}
          </Card>
        </div>
      )}

      {/* Archived accounts section — full width below the two-column grid */}
      {blownAccounts.length > 0 && (
        <div style={{ gridColumn: "1 / -1", marginTop: 8 }}>
          <Card>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4, color: C.muted }}>
              💀 Blown / Archived Accounts ({blownAccounts.length})
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 14, fontStyle: "italic" }}>
              Trade history and ROI data are preserved. These accounts are hidden from the dashboard.
            </div>
            {blownAccounts.map(a => (
              <div key={a.id} style={{
                display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                padding: "12px 0", borderBottom: `1px solid ${C.border}20`,
              }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.sub }}>{a.name}</span>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: "2px 7px",
                      background: C.red + "20", color: C.red,
                      borderRadius: 4, letterSpacing: "0.06em",
                    }}>
                      BLOWN
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 3, lineHeight: 1.6 }}>
                    {a.prop_firm && <span style={{ color: C.blue, marginRight: 6 }}>{a.prop_firm}</span>}
                    {a.account_type?.toUpperCase()} • {fmt(a.starting_balance)}
                    {a.blown_at && ` • Blown ${new Date(a.blown_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`}
                    {a.blown_reason && <span style={{ color: C.red, marginLeft: 6 }}>— {a.blown_reason}</span>}
                  </div>
                </div>
                <button
                  onClick={() => restoreAccount(a.id)}
                  style={{ color: C.green, background: "none", border: "none", cursor: "pointer", fontSize: 12, flexShrink: 0 }}
                >
                  Restore
                </button>
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// BLOWN ACCOUNT MODAL
// ─────────────────────────────────────────────────────────────
function BlownAccountModal({ account, onClose, onConfirm }) {
  const today = new Date().toISOString().split("T")[0];
  const [blownAt, setBlownAt] = useState(today);
  const [reason,  setReason]  = useState("");
  const [saving,  setSaving]  = useState(false);

  const handleConfirm = async () => {
    setSaving(true);
    await onConfirm({ accountId: account.id, blownAt, reason });
    setSaving(false);
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#0f1322", border: `1px solid ${C.border}`,
          borderRadius: 12, padding: 24, maxWidth: 440, width: "100%",
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ fontSize: 16, fontWeight: 800, color: C.red, marginBottom: 6 }}>
          💀 Mark Account as Blown
        </div>
        <div style={{ fontSize: 13, color: C.sub, marginBottom: 20, lineHeight: 1.55 }}>
          <strong style={{ color: C.text }}>{account.name}</strong> will be archived.
          Your trade history and ROI data are preserved — the account just disappears
          from your active dashboard.
        </div>

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>
          Date Blown
        </label>
        <input
          type="date"
          value={blownAt}
          onChange={e => setBlownAt(e.target.value)}
          style={inp({ marginBottom: 14 })}
        />

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>
          Reason (optional)
        </label>
        <input
          placeholder="e.g. Hit max drawdown, revenge traded, news event"
          value={reason}
          onChange={e => setReason(e.target.value)}
          style={inp({ marginBottom: 20 })}
        />

        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: "10px", background: "transparent",
              border: `1px solid ${C.border}`, borderRadius: 8,
              color: C.muted, fontSize: 13, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving}
            style={{
              flex: 1, padding: "10px",
              background: saving ? C.border : C.red,
              border: "none", borderRadius: 8,
              color: "#fff", fontSize: 13, fontWeight: 700,
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? "Archiving…" : "Mark as Blown"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PASS ACCOUNT MODAL — eval/combine passed → spins up linked Funded account
// ─────────────────────────────────────────────────────────────
function PassAccountModal({ account, onClose, onConfirm }) {
  const today = new Date().toISOString().slice(0, 10);
  const [passedAt, setPassedAt] = useState(today);
  const [saving, setSaving] = useState(false);

  // New Funded account fields — prop firm/platform inherited from the
  // source eval/combine since that doesn't change; everything else
  // (size, split, compliance) is new because funded terms differ.
  const [name,    setName]    = useState(account.name ? `${account.name} (Funded)` : "");
  const [balance, setBalance] = useState(account.starting_balance || 50000);
  const [split,   setSplit]   = useState(80);

  const [ddLimit,        setDdLimit]        = useState("");
  const [ddType,         setDdType]         = useState("eod_trailing");
  const [dailyLossLimit, setDailyLossLimit] = useState("");
  const [consistencyRule, setConsistencyRule] = useState("");
  const [autoFillCompliance, setAutoFillCompliance] = useState(false);

  const applyFirmPreset = (bal) => {
    const preset = getPresetForFirmAndBalance(account.prop_firm, bal);
    if (!preset) return;
    setDdLimit(preset.drawdown_limit != null ? String(preset.drawdown_limit) : "");
    setDdType(preset.drawdown_type || "eod_trailing");
    setDailyLossLimit(preset.daily_loss_limit != null ? String(preset.daily_loss_limit) : "");
    setConsistencyRule(preset.consistency_pct != null ? String(preset.consistency_pct) : "");
  };

  useEffect(() => {
    if (autoFillCompliance && balance) applyFirmPreset(balance);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFillCompliance, balance]);

  const handleConfirm = async () => {
    if (!name.trim()) return alert("Name the new funded account");
    setSaving(true);
    await onConfirm({
      sourceAccount: account,
      passedAt,
      funded: {
        name,
        broker: account.broker || null,
        starting_balance: parseFloat(balance) || 0,
        payout_split: parseFloat(split) / 100 || 0.80,
        drawdown_limit: parseFloat(ddLimit) || null,
        drawdown_type: ddType || "eod_trailing",
        daily_loss_limit: parseFloat(dailyLossLimit) || null,
        consistency_rule: consistencyRule !== "" ? parseFloat(consistencyRule) / 100 : null,
      },
    });
    setSaving(false);
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#0f1322", border: `1px solid ${C.border}`,
          borderRadius: 12, padding: 24, maxWidth: 480, width: "100%",
          maxHeight: "90vh", overflowY: "auto",
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 6 }}>
          🎓 Mark "{account.name}" as Passed
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 18, lineHeight: 1.6 }}>
          This account and its trade history stay exactly as they are — nothing is deleted.
          We'll create a new linked Funded account below to carry forward.
        </div>

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Date Passed</label>
        <input type="date" value={passedAt} onChange={e => setPassedAt(e.target.value)} style={inp()} />

        <div style={{
          marginTop: 14, marginBottom: 4, fontSize: 11, fontWeight: 700,
          color: "#60a5fa", textTransform: "uppercase", letterSpacing: "0.08em",
        }}>
          New Funded Account
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>
          {account.prop_firm ? `${account.prop_firm} • ` : ""}{account.broker || "same platform"} (inherited from "{account.name}")
        </div>

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Account Name</label>
        <input value={name} onChange={e => setName(e.target.value)} style={inp()} />

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Starting Balance ($)</label>
        <input type="number" value={balance} onChange={e => setBalance(e.target.value)} style={inp()} />

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>
          Your payout split (%) — e.g. 80 means you keep 80%
        </label>
        <input type="number" min={0} max={100} value={split} onChange={e => setSplit(e.target.value)} style={inp()} />

        <div style={{ marginTop: 6, padding: "12px", background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#60a5fa", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Compliance Setup
            </div>
            {FIRM_COMPLIANCE_PRESETS[account.prop_firm] && (
              <button
                onClick={() => {
                  const next = !autoFillCompliance;
                  setAutoFillCompliance(next);
                  if (next) applyFirmPreset(balance);
                  else { setDdLimit(""); setDdType("eod_trailing"); setDailyLossLimit(""); setConsistencyRule(""); }
                }}
                style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "4px 10px",
                  background: autoFillCompliance ? "rgba(59,130,246,0.18)" : "transparent",
                  border: `1px solid ${autoFillCompliance ? "#60a5fa" : C.border}`,
                  borderRadius: 6, color: autoFillCompliance ? "#60a5fa" : C.muted,
                  fontSize: 11, fontWeight: 600, cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap",
                }}
              >
                Auto-fill from {account.prop_firm}
              </button>
            )}
          </div>

          <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Max Drawdown Limit ($)</label>
          <input type="number" value={ddLimit} onChange={e => setDdLimit(e.target.value)} placeholder="e.g. 2500" style={inp()} />

          <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Drawdown Type</label>
          <select value={ddType} onChange={e => setDdType(e.target.value)} style={inp()}>
            <option value="eod_trailing">EOD Trailing (most common — Topstep, Apex, MFF)</option>
            <option value="static">Static (floor never moves)</option>
            <option value="intraday_trailing">Intraday Trailing (real-time — Apex Intraday)</option>
          </select>

          <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Daily Loss Limit ($) <span style={{ color: C.muted, fontWeight: 400 }}>(optional)</span></label>
          <input type="number" value={dailyLossLimit} onChange={e => setDailyLossLimit(e.target.value)} placeholder="e.g. 1000" style={inp()} />

          <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Consistency Rule (%) <span style={{ color: C.muted, fontWeight: 400 }}>(optional)</span></label>
          <input type="number" min={0} max={100} value={consistencyRule} onChange={e => setConsistencyRule(e.target.value)} placeholder="e.g. 50" style={inp()} />
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <Btn variant="ghost" onClick={onClose} style={{ flex: 1 }}>Cancel</Btn>
          <Btn onClick={handleConfirm} disabled={saving} style={{ flex: 1 }}>
            {saving ? "Converting…" : "Confirm & Create Funded Account"}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// EDIT ACCOUNT MODAL
// ─────────────────────────────────────────────────────────────
function EditAccountModal({ account, onClose, onSaved }) {
  const isKnownFirm = KNOWN_FIRMS.includes(account.prop_firm);
  const isKnownPlat = KNOWN_PLATFORMS.includes(account.broker);

  const [name,       setName]       = useState(account.name || "");
  const [type,       setType]       = useState(account.account_type || "funded");
  const [balance,    setBalance]    = useState(account.starting_balance || 0);
  const [split,      setSplit]      = useState(Math.round((account.payout_split || 0.80) * 100));

  const [firmSelect, setFirmSelect] = useState(isKnownFirm ? account.prop_firm : (account.prop_firm ? "Other" : ""));
  const [firmCustom, setFirmCustom] = useState(!isKnownFirm ? account.prop_firm || "" : "");

  const [platSelect, setPlatSelect] = useState(isKnownPlat ? account.broker : (account.broker ? "Other" : ""));
  const [platCustom, setPlatCustom] = useState(!isKnownPlat ? account.broker || "" : "");

  const [saving, setSaving] = useState(false);

  // compliance
  const [ddLimit,         setDdLimit]         = useState(account.drawdown_limit != null ? String(account.drawdown_limit) : "");
  const [ddType,          setDdType]          = useState(account.drawdown_type || "eod_trailing");
  const [dailyLossLimit,  setDailyLossLimit]  = useState(account.daily_loss_limit != null ? String(account.daily_loss_limit) : "");
  const [profitTarget,    setProfitTarget]    = useState(account.profit_target != null ? String(account.profit_target) : "");
  const [consistencyRule, setConsistencyRule] = useState(account.consistency_rule != null ? String(Math.round(account.consistency_rule * 100)) : "");
  const [autoFillCompliance, setAutoFillCompliance] = useState(false);

  const firmValue = firmSelect === "Other" ? firmCustom : firmSelect;

  const applyFirmPreset = (firm, bal) => {
    const preset = getPresetForFirmAndBalance(firm, bal);
    if (!preset) return;
    setDdLimit(preset.drawdown_limit != null ? String(preset.drawdown_limit) : "");
    setDdType(preset.drawdown_type || "eod_trailing");
    setDailyLossLimit(preset.daily_loss_limit != null ? String(preset.daily_loss_limit) : "");
    setProfitTarget(preset.profit_target != null ? String(preset.profit_target) : "");
    setConsistencyRule(preset.consistency_pct != null ? String(preset.consistency_pct) : "");
  };

  useEffect(() => {
    if (autoFillCompliance && firmValue && balance) {
      applyFirmPreset(firmValue, balance);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFillCompliance, firmValue, balance]);
  const platValue = platSelect === "Other" ? platCustom : platSelect;

  const save = async () => {
    if (!name.trim()) return alert("Account name is required");
    if (firmSelect === "Other" && !firmCustom.trim()) return alert("Enter the prop firm name");
    if (platSelect === "Other" && !platCustom.trim()) return alert("Enter the platform name");
    if (type === "prop" && !profitTarget.toString().trim()) return alert("Target Goal is required for a Prop Eval account");
    setSaving(true);
    try {
      const res = await authFetch(`${SUPABASE_URL}/rest/v1/accounts?id=eq.${account.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Prefer: "return=minimal" },
        body: JSON.stringify({
          name:             name.trim(),
          account_type:     type,
          starting_balance: parseFloat(balance) || 0,
          payout_split:     parseFloat(split) / 100 || 0.80,
          prop_firm:        firmValue.trim() || null,
          broker:           platValue.trim() || null,
          drawdown_limit:   parseFloat(ddLimit) || null,
          drawdown_type:    ddType || "eod_trailing",
          daily_loss_limit: parseFloat(dailyLossLimit) || null,
          profit_target:    parseFloat(profitTarget) || null,
          consistency_rule: consistencyRule !== "" ? parseFloat(consistencyRule) / 100 : null,
        }),
      });
      if (res.ok) onSaved();
      else { const e = await res.json(); alert("Failed: " + (e.message || e.error)); }
    } catch (e) {
      alert("Network error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#0f1322", border: `1px solid ${C.border}`,
          borderRadius: 12, padding: 24, maxWidth: 480, width: "100%",
          maxHeight: "90vh", overflowY: "auto",
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 18 }}>
          Edit Account
        </div>

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Account Name</label>
        <input value={name} onChange={e => setName(e.target.value)} style={inp()} />

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Prop Firm</label>
        <select value={firmSelect} onChange={e => { setFirmSelect(e.target.value); setFirmCustom(""); setAutoFillCompliance(false); }} style={inp()}>
          <option value="">— Select firm —</option>
          {KNOWN_FIRMS.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        {firmSelect === "Other" && (
          <input
            placeholder="Enter prop firm name"
            value={firmCustom}
            onChange={e => setFirmCustom(e.target.value)}
            style={inp({ borderColor: C.blue, marginTop: -4 })}
            autoFocus
          />
        )}

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Account Type</label>
        <select value={type} onChange={e => setType(e.target.value)} style={inp()}>
          <option value="live">Live Account</option>
          <option value="sim">Sim Account</option>
          <option value="prop">Prop Eval</option>
          <option value="funded">Funded Account</option>
        </select>

        {type === "prop" && (
          <>
            <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>
              Target Goal ($) <span style={{ color: C.red }}>*</span>
              <span style={{ color: C.muted, fontWeight: 400 }}> — amount required to pass this evaluation</span>
            </label>
            <input
              type="number"
              value={profitTarget}
              onChange={e => setProfitTarget(e.target.value)}
              placeholder="e.g. 3000"
              style={inp({ borderColor: !profitTarget.toString().trim() ? C.red + "80" : C.border })}
            />
          </>
        )}

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Platform</label>
        <select value={platSelect} onChange={e => { setPlatSelect(e.target.value); setPlatCustom(""); }} style={inp()}>
          <option value="">— Select platform —</option>
          {KNOWN_PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        {platSelect === "Other" && (
          <input
            placeholder="Enter platform name"
            value={platCustom}
            onChange={e => setPlatCustom(e.target.value)}
            style={inp({ borderColor: C.blue, marginTop: -4 })}
          />
        )}

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Starting Balance ($)</label>
        <input type="number" value={balance} onChange={e => setBalance(e.target.value)} style={inp()} />

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>
          Payout Split (%) — e.g. 80 means you keep 80%
        </label>
        <input type="number" min={0} max={100} value={split} onChange={e => setSplit(e.target.value)} style={inp()} />

        {/* ── Compliance Setup ──────────────────────────────── */}
        <div style={{ marginTop: 10, padding: "12px", background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 8 }}>
          {/* Header row with toggle */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#60a5fa", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Compliance Setup
            </div>
            {FIRM_COMPLIANCE_PRESETS[firmValue] && (
              <button
                onClick={() => {
                  const next = !autoFillCompliance;
                  setAutoFillCompliance(next);
                  if (next) applyFirmPreset(firmValue, balance);
                  else {
                    setDdLimit(""); setDdType("eod_trailing");
                    setDailyLossLimit(""); setProfitTarget(""); setConsistencyRule("");
                  }
                }}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "4px 10px",
                  background: autoFillCompliance ? "rgba(59,130,246,0.18)" : "transparent",
                  border: `1px solid ${autoFillCompliance ? "#60a5fa" : C.border}`,
                  borderRadius: 6,
                  color: autoFillCompliance ? "#60a5fa" : C.muted,
                  fontSize: 11, fontWeight: 600, cursor: "pointer",
                  flexShrink: 0, whiteSpace: "nowrap",
                }}
              >
                <span style={{
                  display: "inline-block", width: 28, height: 16, borderRadius: 8,
                  background: autoFillCompliance ? "#3b82f6" : C.border,
                  position: "relative", transition: "background 0.2s", flexShrink: 0,
                }}>
                  <span style={{
                    position: "absolute", top: 2,
                    left: autoFillCompliance ? 14 : 2,
                    width: 12, height: 12, borderRadius: "50%",
                    background: "#fff", transition: "left 0.2s",
                  }} />
                </span>
                Auto-fill from {firmValue}
              </button>
            )}
          </div>

          {autoFillCompliance && FIRM_COMPLIANCE_PRESETS[firmValue] && (
            <div style={{
              fontSize: 11, color: "#60a5fa",
              background: "rgba(59,130,246,0.08)",
              border: "1px solid rgba(59,130,246,0.2)",
              borderRadius: 6, padding: "6px 10px", marginBottom: 10, lineHeight: 1.5,
            }}>
              ✓ Pre-filled from <strong>{firmValue}</strong> defaults for a {
                new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(parseFloat(balance) || 0)
              } account. You can override any field below.
            </div>
          )}

          <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Max Drawdown Limit ($)</label>
          <input type="number" value={ddLimit} onChange={e => setDdLimit(e.target.value)} placeholder="e.g. 2500" style={inp()} />

          <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Drawdown Type</label>
          <select value={ddType} onChange={e => setDdType(e.target.value)} style={inp()}>
            <option value="eod_trailing">EOD Trailing (Topstep, Apex, MFF)</option>
            <option value="static">Static (floor never moves)</option>
            <option value="intraday_trailing">Intraday Trailing (Apex Intraday)</option>
          </select>

          <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Daily Loss Limit ($)</label>
          <input type="number" value={dailyLossLimit} onChange={e => setDailyLossLimit(e.target.value)} placeholder="e.g. 1000" style={inp()} />

          {type !== "prop" && (
            <>
              <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Profit Target ($)</label>
              <input type="number" value={profitTarget} onChange={e => setProfitTarget(e.target.value)} placeholder="e.g. 3000" style={inp()} />
            </>
          )}

          <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Consistency Rule (%) — 0 = none</label>
          <input type="number" min={0} max={100} value={consistencyRule} onChange={e => setConsistencyRule(e.target.value)} placeholder="e.g. 50" style={inp()} />

          {/* Disclaimer */}
          <div style={{
            marginTop: 10,
            padding: "8px 10px",
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 6,
            fontSize: 11,
            color: C.muted,
            lineHeight: 1.6,
          }}>
            📋 You are responsible for keeping these settings current. Prop firm rules can change — verify and update accordingly against your firm's dashboard any time you receive a rule update notice and before you trade.
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: "10px", background: "transparent",
              border: `1px solid ${C.border}`, borderRadius: 8,
              color: C.muted, fontSize: 13, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <Btn onClick={save} disabled={saving} style={{ flex: 1 }}>
            {saving ? "Saving…" : "Save Changes"}
          </Btn>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
function ROITab({ accounts, payouts, expenses, loading }) {
  const [roiPeriod, setRoiPeriod] = useState("all");
  const [expandedFirms, setExpandedFirms] = useState({});

  const toggleFirm = (firm) => setExpandedFirms(prev => ({ ...prev, [firm]: !prev[firm] }));

  // Group accounts by prop firm — include per-account transaction breakdown
  const firmGroups = useMemo(() => {
    const groups = {};
    accounts.forEach(a => {
      const firm = a.prop_firm || "Unassigned";
      if (!groups[firm]) groups[firm] = { accounts: [], payouts: 0, expenses: 0, byAccount: {} };
      groups[firm].accounts.push(a);
      groups[firm].byAccount[a.id] = { name: a.name, payouts: 0, expenses: 0 };
    });
    payouts.forEach(p => {
      const acct = accounts.find(a => a.id === p.account_id);
      const firm = acct?.prop_firm || "Unassigned";
      if (groups[firm]) {
        groups[firm].payouts += parseFloat(p.amount) || 0;
        if (groups[firm].byAccount[p.account_id]) {
          groups[firm].byAccount[p.account_id].payouts += parseFloat(p.amount) || 0;
        }
      }
    });
    expenses.forEach(e => {
      const acct = accounts.find(a => a.id === e.account_id);
      const firm = acct?.prop_firm || "Unassigned";
      if (groups[firm]) {
        groups[firm].expenses += parseFloat(e.amount) || 0;
        if (groups[firm].byAccount[e.account_id]) {
          groups[firm].byAccount[e.account_id].expenses += parseFloat(e.amount) || 0;
        }
      }
    });
    return groups;
  }, [accounts, payouts, expenses]);

  const totalPayouts  = payouts.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const totalExpenses = expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const totalNet      = totalPayouts - totalExpenses;
  const totalROI      = totalExpenses > 0 ? (totalNet / totalExpenses) * 100 : 0;

  // Build monthly chart data
  const monthlyData = useMemo(() => {
    const map = {};
    payouts.forEach(p => {
      const m = p.date?.slice(0, 7);
      if (!m) return;
      if (!map[m]) map[m] = { month: m, payouts: 0, expenses: 0 };
      map[m].payouts += parseFloat(p.amount) || 0;
    });
    expenses.forEach(e => {
      const m = e.date?.slice(0, 7);
      if (!m) return;
      if (!map[m]) map[m] = { month: m, payouts: 0, expenses: 0 };
      map[m].expenses += parseFloat(e.amount) || 0;
    });
    return Object.values(map).sort((a, b) => a.month.localeCompare(b.month));
  }, [payouts, expenses]);

  // Filter payouts/expenses by selected period for firm ROI chart
  const firmROIData = useMemo(() => {
    const now = new Date();
    const cutoff = (() => {
      if (roiPeriod === "week")    { const d = new Date(now); d.setDate(d.getDate() - 7); return d; }
      if (roiPeriod === "month")   { const d = new Date(now); d.setMonth(d.getMonth() - 1); return d; }
      if (roiPeriod === "quarter") { const d = new Date(now); d.setMonth(d.getMonth() - 3); return d; }
      if (roiPeriod === "year")    { const d = new Date(now); d.setFullYear(d.getFullYear() - 1); return d; }
      return null;
    })();

    const filterByDate = (items) => cutoff
      ? items.filter(i => i.date && new Date(i.date) >= cutoff)
      : items;

    const filteredPayouts  = filterByDate(payouts);
    const filteredExpenses = filterByDate(expenses);

    const map = {};
    filteredPayouts.forEach(p => {
      const acct = accounts.find(a => a.id === p.account_id);
      const firm = acct?.prop_firm || "Unassigned";
      if (!map[firm]) map[firm] = { payouts: 0, expenses: 0 };
      map[firm].payouts += parseFloat(p.amount) || 0;
    });
    filteredExpenses.forEach(e => {
      const acct = accounts.find(a => a.id === e.account_id);
      const firm = acct?.prop_firm || "Unassigned";
      if (!map[firm]) map[firm] = { payouts: 0, expenses: 0 };
      map[firm].expenses += parseFloat(e.amount) || 0;
    });

    return Object.entries(map)
      .map(([name, d]) => ({
        name,
        net: d.payouts - d.expenses,
        roi: d.expenses > 0 ? ((d.payouts - d.expenses) / d.expenses) * 100 : null,
        payouts: d.payouts,
        expenses: d.expenses,
      }))
      .filter(f => f.payouts > 0 || f.expenses > 0)
      .sort((a, b) => (b.roi ?? -999) - (a.roi ?? -999));
  }, [payouts, expenses, accounts, roiPeriod]);

  if (loading) return <div style={{ color: C.muted, fontSize: 13 }}>Loading…</div>;

  if (payouts.length === 0 && expenses.length === 0) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "40px 20px", color: C.muted }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 8 }}>No data yet</div>
          <div style={{ fontSize: 13 }}>Log your first payout or expense in the Payouts & Expenses tab to see your ROI.</div>
        </div>
      </Card>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Summary KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        {[
          { label: "Total Payouts",  value: fmt(totalPayouts),  color: C.green },
          { label: "Total Expenses", value: fmt(totalExpenses), color: C.red   },
          { label: "Net Profit",     value: fmt(totalNet),      color: totalNet >= 0 ? C.green : C.red },
          { label: "Overall ROI",    value: pct(totalROI),      color: totalROI >= 0 ? C.green : C.red },
        ].map(k => (
          <Card key={k.label} style={{ textAlign: "center", padding: 16 }}>
            <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
              {k.label}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: k.color }}>{k.value}</div>
          </Card>
        ))}
      </div>

      {/* Per-firm breakdown */}
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Income by Prop Firm</div>
          <div style={{ fontSize: 11, color: C.muted, fontStyle: "italic" }}>
            Reflects logged payouts & expenses only — not unrealized trading gains.
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ color: C.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                {["Firm", "Accounts", "Payouts", "Expenses", "Net", "ROI"].map(h => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: h === "Firm" || h === "Accounts" ? "left" : "right", borderBottom: `1px solid ${C.border}`, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(firmGroups).map(([firm, data]) => {
                const net = data.payouts - data.expenses;
                const roi = data.expenses > 0 ? (net / data.expenses) * 100 : 0;
                const isExpanded = !!expandedFirms[firm];
                const acctRows = Object.values(data.byAccount);
                const hasActivity = acctRows.some(a => a.payouts > 0 || a.expenses > 0);
                return (
                  <React.Fragment key={firm}>
                    <tr
                      key={firm}
                      onClick={() => hasActivity && toggleFirm(firm)}
                      style={{
                        borderBottom: `1px solid ${C.border}`,
                        cursor: hasActivity ? "pointer" : "default",
                        background: isExpanded ? "rgba(59,130,246,0.05)" : "transparent",
                      }}
                    >
                      <td style={{ padding: "12px", fontWeight: 700, color: C.text }}>
                        <span style={{ marginRight: 8, fontSize: 10, color: C.muted }}>
                          {hasActivity ? (isExpanded ? "▼" : "▶") : ""}
                        </span>
                        {firm}
                      </td>
                      <td style={{ padding: "12px", color: C.muted }}>{data.accounts.length}</td>
                      <td style={{ padding: "12px", textAlign: "right", color: C.green }}>{fmt(data.payouts)}</td>
                      <td style={{ padding: "12px", textAlign: "right", color: C.red }}>{fmt(data.expenses)}</td>
                      <td style={{ padding: "12px", textAlign: "right", color: net >= 0 ? C.green : C.red, fontWeight: 700 }}>{fmt(net)}</td>
                      <td style={{ padding: "12px", textAlign: "right", color: roi >= 0 ? C.green : C.red, fontWeight: 700 }}>{pct(roi)}</td>
                    </tr>
                    {isExpanded && acctRows.map(a => {
                      const aNet = a.payouts - a.expenses;
                      const aRoi = a.expenses > 0 ? (aNet / a.expenses) * 100 : null;
                      return (
                        <tr key={a.name} style={{ borderBottom: `1px solid ${C.border}20`, background: "rgba(59,130,246,0.03)" }}>
                          <td style={{ padding: "8px 12px 8px 32px", color: C.muted, fontSize: 12 }}>↳ {a.name}</td>
                          <td style={{ padding: "8px 12px", color: C.muted, fontSize: 12 }}>—</td>
                          <td style={{ padding: "8px 12px", textAlign: "right", color: C.green, fontSize: 12 }}>{fmt(a.payouts)}</td>
                          <td style={{ padding: "8px 12px", textAlign: "right", color: C.red, fontSize: 12 }}>{fmt(a.expenses)}</td>
                          <td style={{ padding: "8px 12px", textAlign: "right", color: aNet >= 0 ? C.green : C.red, fontSize: 12, fontWeight: 600 }}>{fmt(aNet)}</td>
                          <td style={{ padding: "8px 12px", textAlign: "right", color: aRoi !== null ? (aRoi >= 0 ? C.green : C.red) : C.muted, fontSize: 12, fontWeight: 600 }}>
                            {aRoi !== null ? pct(aRoi) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              {/* Totals row */}
              <tr style={{ background: "#1a1d2e" }}>
                <td style={{ padding: "12px", fontWeight: 800, color: C.text }}>TOTAL</td>
                <td style={{ padding: "12px", color: C.muted }}>{accounts.length}</td>
                <td style={{ padding: "12px", textAlign: "right", color: C.green, fontWeight: 700 }}>{fmt(totalPayouts)}</td>
                <td style={{ padding: "12px", textAlign: "right", color: C.red, fontWeight: 700 }}>{fmt(totalExpenses)}</td>
                <td style={{ padding: "12px", textAlign: "right", color: totalNet >= 0 ? C.green : C.red, fontWeight: 800 }}>{fmt(totalNet)}</td>
                <td style={{ padding: "12px", textAlign: "right", color: totalROI >= 0 ? C.green : C.red, fontWeight: 800 }}>{pct(totalROI)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Firm ROI Chart */}
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>ROI by Prop Firm</div>
          <div style={{ display: "flex", gap: 4 }}>
            {[["week","1W"],["month","1M"],["quarter","3M"],["year","1Y"],["all","All"]].map(([val, label]) => (
              <button
                key={val}
                onClick={() => setRoiPeriod(val)}
                style={{
                  padding: "4px 10px", fontSize: 11, fontWeight: 600,
                  background: roiPeriod === val ? C.blue : "transparent",
                  border: `1px solid ${roiPeriod === val ? C.blue : C.border}`,
                  borderRadius: 6,
                  color: roiPeriod === val ? "#fff" : C.muted,
                  cursor: "pointer",
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {firmROIData.length === 0 ? (
          <div style={{ textAlign: "center", padding: "24px 0", color: C.muted, fontSize: 13 }}>
            No payouts or expenses logged in this period.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {firmROIData.map(f => {
              const roi = f.roi ?? 0;
              const isPos = roi >= 0;
              const barPct = Math.min(Math.abs(roi) / Math.max(...firmROIData.map(x => Math.abs(x.roi ?? 0)), 1) * 100, 100);
              return (
                <div key={f.name}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{f.name}</div>
                    <div style={{ display: "flex", gap: 16, alignItems: "baseline" }}>
                      <span style={{ fontSize: 11, color: C.muted }}>{fmt(f.payouts)} payouts · {fmt(f.expenses)} costs</span>
                      <span style={{ fontSize: 14, fontWeight: 800, color: isPos ? C.green : C.red, fontFamily: "monospace" }}>
                        {f.roi !== null ? `${isPos ? "+" : ""}${roi.toFixed(0)}%` : "—"}
                      </span>
                    </div>
                  </div>
                  <div style={{ height: 8, background: C.border, borderRadius: 4, overflow: "hidden" }}>
                    <div style={{
                      height: "100%",
                      width: `${barPct}%`,
                      background: isPos ? C.green : C.red,
                      borderRadius: 4,
                      transition: "width 0.4s ease",
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Payout History chart */}
      {monthlyData.length > 0 && (
        <Card>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14, color: C.text }}>Payout History</div>
          <PayoutChart data={monthlyData} />
        </Card>
      )}
    </div>
  );
}
// ─── Simple SVG bar chart ─────────────────────────────────────
function PayoutChart({ data }) {
  const maxVal = Math.max(...data.map(d => Math.max(d.payouts, d.expenses)), 1);
  const W = 600, H = 200, PAD = 40, BAR_W = Math.min(32, (W - PAD * 2) / (data.length * 2.5));
  const BOTTOM_PAD = 30;

  const formatMonth = (m) => {
    const [y, mo] = m.split("-");
    return ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][parseInt(mo) - 1] + " " + y.slice(2);
  };

  return (
    <div style={{ overflowX: "auto" }}>
      <svg viewBox={`0 0 ${W} ${H + PAD + BOTTOM_PAD}`} style={{ width: "100%", maxWidth: W, display: "block" }}>
        {/* Y gridlines */}
        {[0, 0.25, 0.5, 0.75, 1].map(t => (
          <g key={t}>
            <line x1={PAD} y1={PAD + (1 - t) * H} x2={W - PAD} y2={PAD + (1 - t) * H} stroke={C.border} strokeWidth={0.5} />
            <text x={PAD - 4} y={PAD + (1 - t) * H + 4} fontSize={9} fill={C.muted} textAnchor="end">
              ${(maxVal * t / 1000).toFixed(0)}k
            </text>
          </g>
        ))}

        {/* Bars */}
        {data.map((d, i) => {
          const slotW = (W - PAD * 2) / data.length;
          const cx = PAD + i * slotW + slotW / 2;
          const ph = (d.payouts / maxVal) * H;
          const eh = (d.expenses / maxVal) * H;
          return (
            <g key={d.month}>
              {/* Payout bar */}
              <rect x={cx - BAR_W - 2} y={PAD + H - ph} width={BAR_W} height={ph}
                fill={C.green} fillOpacity={0.8} rx={2} />
              {/* Expense bar */}
              <rect x={cx + 2} y={PAD + H - eh} width={BAR_W} height={eh}
                fill={C.red} fillOpacity={0.8} rx={2} />
              {/* Month label */}
              <text x={cx} y={PAD + H + 18} fontSize={9} fill={C.muted} textAnchor="middle">
                {formatMonth(d.month)}
              </text>
            </g>
          );
        })}

        {/* Legend */}
        <rect x={W - 120} y={8} width={10} height={10} fill={C.green} fillOpacity={0.8} rx={2} />
        <text x={W - 106} y={17} fontSize={10} fill={C.muted}>Payouts</text>
        <rect x={W - 60} y={8} width={10} height={10} fill={C.red} fillOpacity={0.8} rx={2} />
        <text x={W - 46} y={17} fontSize={10} fill={C.muted}>Expenses</text>
      </svg>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TAB 3 — PAYOUTS & EXPENSES
// ─────────────────────────────────────────────────────────────
function TxnsTab({ userId, accounts, payouts, expenses, fetchTxns, fetchAccounts, loading }) {
  const [mode, setMode]           = useState("payout"); // "payout" | "expense"
  const [acctId, setAcctId]       = useState("");
  const [amount, setAmount]       = useState("");
  const [date, setDate]           = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes]         = useState("");
  const [expType, setExpType]     = useState("eval_fee");
  const [saving, setSaving]       = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetBal, setResetBal]   = useState("");

  const selectedAccount = accounts.find(a => a.id === acctId);

  const save = async () => {
    if (!acctId) return alert("Select an account");
    if (!amount || isNaN(parseFloat(amount))) return alert("Enter a valid amount");
    setSaving(true);
    try {
      const table = mode === "payout" ? "payouts" : "expenses";
      const payload = {
        user_id:    userId,
        account_id: acctId,
        amount:     parseFloat(amount),
        date,
        notes:      notes.trim() || null,
        ...(mode === "expense" ? { expense_type: expType } : {}),
      };
      const res = await authFetch(`${SUPABASE_URL}/rest/v1/${table}`, {
        method: "POST",
        headers: { Prefer: "return=representation" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.message || e.error); }

      // After logging a payout, prompt for balance reset
      if (mode === "payout") {
        setShowReset(true);
        setResetBal(selectedAccount?.starting_balance || "");
      } else {
        setAmount(""); setNotes("");
        fetchTxns();
      }
    } catch (e) {
      alert("Failed: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const applyReset = async (newBalance) => {
    if (!newBalance || isNaN(parseFloat(newBalance))) return alert("Enter a valid balance");
    try {
      await authFetch(`${SUPABASE_URL}/rest/v1/accounts?id=eq.${acctId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Prefer: "return=minimal" },
        body: JSON.stringify({ starting_balance: parseFloat(newBalance) }),
      });
      setShowReset(false);
      setAmount(""); setNotes("");
      fetchTxns();
      fetchAccounts();
    } catch (e) {
      alert("Failed to reset balance: " + e.message);
    }
  };

  const deleteTxn = async (id, table) => {
    if (!window.confirm("Delete this entry?")) return;
    await authFetch(`${SUPABASE_URL}/rest/v1/${table}?id=eq.${id}`, { method: "DELETE" });
    fetchTxns();
  };

  const EXPENSE_TYPES = {
    eval_fee:    "Eval Fee",
    monthly_fee: "Monthly Fee",
    reset_fee:   "Reset Fee",
    other:       "Other",
  };

  // Balance reset modal
  if (showReset && selectedAccount) {
    const originalSize = selectedAccount.starting_balance;
    return (
      <Card style={{ maxWidth: 460, margin: "0 auto" }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 8 }}>
          💰 Payout logged — reset account balance?
        </div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 20, lineHeight: 1.6 }}>
          After a payout, most prop firms reset your account. Choose how to update <strong style={{ color: C.text }}>{selectedAccount.name}</strong>:
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            onClick={() => applyReset(originalSize)}
            style={{
              padding: "12px 16px", background: C.blue + "20", border: `1px solid ${C.blue}50`,
              borderRadius: 8, color: C.text, fontSize: 13, fontWeight: 600, cursor: "pointer", textAlign: "left",
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 2 }}>Reset to original size</div>
            <div style={{ fontSize: 11, color: C.muted }}>Back to {fmt(originalSize)}</div>
          </button>

          <div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>Or enter a custom reset balance:</div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="number"
                placeholder="e.g. 50000"
                value={resetBal}
                onChange={e => setResetBal(e.target.value)}
                style={{ ...inp(), marginBottom: 0, flex: 1 }}
              />
              <Btn onClick={() => applyReset(resetBal)}>Apply</Btn>
            </div>
          </div>

          <button
            onClick={() => { setShowReset(false); setAmount(""); setNotes(""); fetchTxns(); }}
            style={{
              padding: "10px 16px", background: "none", border: `1px solid ${C.border}`,
              borderRadius: 8, color: C.muted, fontSize: 12, cursor: "pointer",
            }}
          >
            Skip — don't change balance
          </button>
        </div>
      </Card>
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 20, alignItems: "start" }}>
      {/* Log form */}
      <Card>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14, color: C.text }}>Log Transaction</div>

        {/* Mode toggle */}
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
          {[["payout", "💰 Payout"], ["expense", "💸 Expense"]].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setMode(id)}
              style={{
                flex: 1, padding: "8px 0", fontSize: 12, fontWeight: 700,
                background: mode === id ? (id === "payout" ? C.green + "20" : C.red + "20") : "transparent",
                border: `1px solid ${mode === id ? (id === "payout" ? C.green : C.red) : C.border}`,
                borderRadius: 6, color: mode === id ? (id === "payout" ? C.green : C.red) : C.muted,
                cursor: "pointer",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Account</label>
        <select value={acctId} onChange={e => setAcctId(e.target.value)} style={inp()}>
          <option value="">— Select account —</option>
          {accounts.map(a => (
            <option key={a.id} value={a.id}>
              {a.prop_firm ? `${a.prop_firm} — ` : ""}{a.name}
            </option>
          ))}
        </select>

        {mode === "expense" && (
          <>
            <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Expense Type</label>
            <select value={expType} onChange={e => setExpType(e.target.value)} style={inp()}>
              {Object.entries(EXPENSE_TYPES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </>
        )}

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Amount ($)</label>
        <input type="number" min={0} placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} style={inp()} />

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Date</label>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inp()} />

        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Notes (optional)</label>
        <input placeholder="e.g. April payout, $100K account" value={notes} onChange={e => setNotes(e.target.value)} style={inp()} />

        <Btn onClick={save} disabled={saving} style={{ width: "100%", marginTop: 4 }}>
          {saving ? "Saving…" : `Log ${mode === "payout" ? "Payout" : "Expense"}`}
        </Btn>
      </Card>

      {/* Transaction history */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Payouts */}
        <Card>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: C.text }}>
            Payouts ({payouts.length})
          </div>
          {loading && <div style={{ color: C.muted, fontSize: 12 }}>Loading…</div>}
          {!loading && payouts.length === 0 && <div style={{ color: C.muted, fontSize: 12 }}>No payouts logged yet.</div>}
          {payouts.map(p => {
            const acct = accounts.find(a => a.id === p.account_id);
            return (
              <div key={p.id} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "10px 0", borderBottom: `1px solid ${C.border}`,
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.green }}>{fmt(p.amount)}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>
                    {acct?.prop_firm && <span style={{ color: C.blue }}>{acct.prop_firm} — </span>}
                    {acct?.name || "Unknown"} • {p.date}
                    {p.notes && ` • ${p.notes}`}
                  </div>
                </div>
                <button onClick={() => deleteTxn(p.id, "payouts")} style={{ color: C.red, background: "none", border: "none", cursor: "pointer", fontSize: 11 }}>
                  Delete
                </button>
              </div>
            );
          })}
        </Card>

        {/* Expenses */}
        <Card>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: C.text }}>
            Expenses ({expenses.length})
          </div>
          {!loading && expenses.length === 0 && <div style={{ color: C.muted, fontSize: 12 }}>No expenses logged yet.</div>}
          {expenses.map(e => {
            const acct = accounts.find(a => a.id === e.account_id);
            return (
              <div key={e.id} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "10px 0", borderBottom: `1px solid ${C.border}`,
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.red }}>{fmt(e.amount)}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>
                    {acct?.prop_firm && <span style={{ color: C.blue }}>{acct.prop_firm} — </span>}
                    {acct?.name || "Unknown"} • {EXPENSE_TYPES[e.expense_type] || e.expense_type} • {e.date}
                    {e.notes && ` • ${e.notes}`}
                  </div>
                </div>
                <button onClick={() => deleteTxn(e.id, "expenses")} style={{ color: C.red, background: "none", border: "none", cursor: "pointer", fontSize: 11 }}>
                  Delete
                </button>
              </div>
            );
          })}
        </Card>
      </div>
    </div>
  );
}
